import type { CookingTechnique } from '@familychef/shared';
import { Edit, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import {
  DIFFICULTIES,
  DIFFICULTY_LABELS,
  TECHNIQUE_CATEGORIES,
  TECHNIQUE_CATEGORY_LABELS,
  useDeleteTechnique,
  useTechniques,
} from '../../hooks/useTechniques';
import type { TechniqueFilters } from '../../hooks/useTechniques';

import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DataTable } from '@/components/common/DataTable';
import { PageHeader } from '@/components/common/PageHeader';
import { SearchBar } from '@/components/common/SearchBar';
import { AppShell } from '@/components/layout/AppShell';
import { TopBar } from '@/components/layout/TopBar';
import { useNavigate } from '@/lib/router';

const PAGE_SIZE = 20;

const difficultyColors = {
  easy: 'bg-green-100 text-green-700',
  medium: 'bg-orange-100 text-orange-700',
  hard: 'bg-red-100 text-red-700',
};

export function TechniquesPage() {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [filters, setFilters] = useState<TechniqueFilters>({
    search: '',
    category: '',
    difficulty: '',
  });
  const [deleteTarget, setDeleteTarget] = useState<CookingTechnique | null>(null);

  const { data, isLoading } = useTechniques(filters, page);
  const deleteTechnique = useDeleteTechnique();
  const totalPages = Math.ceil((data?.total ?? 0) / PAGE_SIZE);

  const columns = [
    {
      key: 'name',
      header: 'Nom',
      cell: (row: CookingTechnique) => (
        <span className="font-medium text-slate-900">{row.name}</span>
      ),
    },
    {
      key: 'category',
      header: 'Catégorie',
      className: 'hidden sm:table-cell',
      cell: (row: CookingTechnique) => (
        <span className="badge bg-slate-100 text-slate-600">
          {TECHNIQUE_CATEGORY_LABELS[row.category]?.fr}
        </span>
      ),
    },
    {
      key: 'difficulty',
      header: 'Difficulté',
      className: 'hidden md:table-cell',
      cell: (row: CookingTechnique) => (
        <span className={`badge ${difficultyColors[row.difficulty]}`}>
          {DIFFICULTY_LABELS[row.difficulty]?.fr}
        </span>
      ),
    },
    {
      key: 'duration',
      header: 'Durée (min)',
      className: 'hidden lg:table-cell',
      cell: (row: CookingTechnique) => (
        <span className="text-slate-600">{row.estimated_duration ?? '—'}</span>
      ),
    },
    {
      key: 'steps',
      header: 'Étapes',
      className: 'hidden lg:table-cell',
      cell: (row: CookingTechnique) => <span className="text-slate-500">{row.steps.length}</span>,
    },
    {
      key: 'actions',
      header: '',
      cell: (row: CookingTechnique) => (
        <div className="flex items-center justify-end gap-1">
          <button
            onClick={() => void navigate({ to: `/techniques/${row.id}/edit` })}
            className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
          >
            <Edit size={15} />
          </button>
          <button
            onClick={() => setDeleteTarget(row)}
            className="rounded-md p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <AppShell>
      <TopBar
        title="Techniques culinaires"
        subtitle={`${data?.total ?? 0} technique${(data?.total ?? 0) > 1 ? 's' : ''}`}
      />
      <div className="p-4 md:p-6">
        <PageHeader
          title="Gestes techniques"
          actions={
            <button
              onClick={() => void navigate({ to: '/techniques/new' })}
              className="flex items-center gap-1.5 rounded-lg bg-green-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-700"
            >
              <Plus size={16} /> Ajouter
            </button>
          }
        />

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <SearchBar
            value={filters.search}
            onChange={(v) => {
              setFilters((f) => ({ ...f, search: v }));
              setPage(0);
            }}
            placeholder="Rechercher une technique..."
          />
          <select
            value={filters.category}
            onChange={(e) => {
              setFilters((f) => ({ ...f, category: e.target.value }));
              setPage(0);
            }}
            className="h-9 w-full sm:w-auto sm:flex-none flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-green-500 focus:outline-none"
          >
            <option value="">Toutes les catégories</option>
            {TECHNIQUE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {TECHNIQUE_CATEGORY_LABELS[c]?.fr}
              </option>
            ))}
          </select>
          <select
            value={filters.difficulty}
            onChange={(e) => {
              setFilters((f) => ({ ...f, difficulty: e.target.value }));
              setPage(0);
            }}
            className="h-9 w-full sm:w-auto sm:flex-none flex-1 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-green-500 focus:outline-none"
          >
            <option value="">Toutes les difficultés</option>
            {DIFFICULTIES.map((d) => (
              <option key={d} value={d}>
                {DIFFICULTY_LABELS[d]?.fr}
              </option>
            ))}
          </select>
        </div>

        <DataTable
          data={data?.data ?? []}
          columns={columns}
          isLoading={isLoading}
          getRowKey={(r) => r.id}
          emptyMessage="Aucune technique trouvée"
        />

        {totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
            <span>
              Page {page + 1} / {totalPages}
            </span>
            <div className="flex gap-1">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Précédent
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1}
                className="rounded-md border border-slate-200 bg-white px-3 py-1.5 disabled:opacity-40 hover:bg-slate-50"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Supprimer la technique"
        message={`Supprimer « ${deleteTarget?.name} » ? Cette action est irréversible.`}
        confirmLabel="Supprimer"
        isDestructive
        isLoading={deleteTechnique.isPending}
        icon="🗑️"
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteTechnique.mutate(deleteTarget.id, { onSuccess: () => setDeleteTarget(null) });
        }}
        onCancel={() => setDeleteTarget(null)}
      />
    </AppShell>
  );
}
